import { ReactNode, useMemo, useState } from 'react';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { DataTable } from 'react-native-paper';

import { colors, spacing } from '../../theme';
import { CheckBox } from '../CheckBox';
import { Pagination, PaginationProps } from './Pagination';
import { Text } from '../Text';
import { NoData } from './NoData';
import { usePages } from './usePages';

const DEFAULT_ROWS_PER_PAGE = 10;

type RenderProps<Entity> = {
	row: Entity;
};

export type ColumnConfig<Entity> =
	| {
			name: string;
			accessor: keyof Entity;
			renderCell?: never;
	  }
	| {
			name: string;
			accessor?: keyof Entity;
			renderCell: (props: RenderProps<Entity>) => ReactNode;
	  };

export type TableProps<Entity> = {
	data: Entity[];
	columnsConfig: ColumnConfig<Entity>[];
	onSelectChange?: (rows: Entity) => unknown;
	onRowPress?: (row: Entity) => unknown;
	renderRowActions?: (props: RenderProps<Entity>) => ReactNode;
	rowActionsWidth?: number;
	style?: StyleProp<ViewStyle>;
} & Partial<Pick<PaginationProps, 'rowsPerPage'>>;

export const Table = <Entity,>({
	data,
	columnsConfig,
	onSelectChange,
	onRowPress,
	renderRowActions,
	rowActionsWidth,
	rowsPerPage = DEFAULT_ROWS_PER_PAGE,
	style,
}: TableProps<Entity>) => {
	type ColumnName = NonNullable<(typeof columnsConfig)[0]['accessor']>;
	const [sortByColumn, setSortByColumn] = useState<{
		name: ColumnName;
		order: 'ascending' | 'descending';
	} | null>(null);
	const showCheckboxes = useMemo(() => onSelectChange !== undefined, [onSelectChange]);
	const { currentPage, numberOfPages, onPageChange } = usePages({
		rowsPerPage,
		itemsCount: data.length,
	});

	const pageRows = useMemo(() => {
		const sortedItems = sortByColumn
			? data.slice().sort((rowA, rowB) => {
					const rowAValue = `${rowA[sortByColumn.name]}`;
					const rowBValue = `${rowB[sortByColumn.name]}`;

					return sortByColumn.order === 'ascending'
						? rowAValue.localeCompare(rowBValue, undefined, { numeric: true })
						: rowBValue.localeCompare(rowAValue, undefined, { numeric: true });
				})
			: data;
		const rowsFrom = currentPage * rowsPerPage;
		const rowsTo = Math.min((currentPage + 1) * rowsPerPage, sortedItems.length);

		return sortedItems.slice(rowsFrom, rowsTo);
	}, [currentPage, data, rowsPerPage, sortByColumn]);

	const onColumnSortPress = (column: ColumnConfig<Entity>) => {
		const defaultOrder = 'ascending';

		if (!column.accessor) {
			return;
		}

		const name = column.accessor;
		if (sortByColumn?.name !== name) {
			return setSortByColumn({
				name,
				order: defaultOrder,
			});
		}

		const newOrder = sortByColumn.order === 'ascending' ? 'descending' : 'ascending';

		return setSortByColumn({
			name,
			order: newOrder,
		});
	};

	return (
		<View style={style}>
			<DataTable style={styles.table}>
				<DataTable.Header style={styles.header}>
					{showCheckboxes && <CheckBox style={styles.checkbox} />}

					{columnsConfig.map((column) => (
						<DataTable.Title
							key={column.name}
							sortDirection={
								sortByColumn && sortByColumn?.name === column.accessor
									? sortByColumn.order
									: undefined
							}
							onPress={() => onColumnSortPress(column)}
						>
							<Text category="p2">{column.name}</Text>
						</DataTable.Title>
					))}
					{
						/* empty view to fix other titles alignment */
						!!renderRowActions && <View style={{ width: rowActionsWidth }} />
					}
				</DataTable.Header>
				{pageRows.length ? (
					pageRows.map((item, index) => {
						const onPress = onRowPress ? () => onRowPress(item) : undefined;

						return (
							<DataTable.Row key={index} style={styles.row} onPress={onPress}>
								{showCheckboxes && <CheckBox style={styles.checkbox} />}
								{columnsConfig.map((columnConfig, index) => (
									<DataTable.Cell key={index}>
										{columnConfig.renderCell ? (
											columnConfig.renderCell({ row: item })
										) : (
											<Text category="p1" numberOfLines={1}>{`${
												item[columnConfig.accessor] ?? ''
											}`}</Text>
										)}
									</DataTable.Cell>
								))}
								{!!renderRowActions && renderRowActions({ row: item })}
							</DataTable.Row>
						);
					})
				) : (
					<NoData />
				)}
			</DataTable>
			<Pagination
				currentPage={currentPage}
				numberOfPages={numberOfPages}
				rowsPerPage={rowsPerPage}
				onPageChange={onPageChange}
			/>
		</View>
	);
};

const styles = StyleSheet.create({
	table: {
		backgroundColor: colors.white,
		borderRadius: 4,
	},
	header: {
		borderBottomColor: colors.black200,
		borderBottomWidth: 1,
	},
	row: {
		borderBottomColor: colors.black200,
		minHeight: 56,
		borderBottomWidth: 1,
	},
	checkbox: {
		margin: spacing(),
	},
});
